import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { GlobalsService } from 'app/globals.service';
import { PasswordStrength, getPasswordStrength } from './password-strength.util';

interface ChangePasswordDialogData {
    force?: boolean;
    oldPassword?: string;
}

/**
 * Interfaccia per il risultato del dialog di cambio password
 * Contiene la vecchia e la nuova password inserite dall'utente
 */
export interface ChangePasswordDialogResult {
    oldPassword: string;
    newPassword: string;
}

/**
 * Componente dialog per il cambio della password dell'utente
 * Consente di inserire la vecchia password e la nuova password con conferma
 * Include un indicatore visuale della forza della password
 * Il dialog è draggabile dall'header
 */
@Component({
    selector: 'change-password-dialog',
    standalone: true,
    templateUrl: './change-password-dialog.component.html',
    styleUrls: ['./change-password-dialog.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        CdkDrag,
        CdkDragHandle,
    ],
})
export class ChangePasswordDialogComponent {
    // Servizi iniettati
    private _fb = inject(FormBuilder).nonNullable;
    private _dialogRef = inject(MatDialogRef<ChangePasswordDialogComponent, ChangePasswordDialogResult | undefined>);
    private _data = inject<ChangePasswordDialogData | null>(MAT_DIALOG_DATA, { optional: true });

    readonly isTermsAcceptanceRequired = this._data?.force === true;
    readonly termsDocumentUrl = GlobalsService.isCloud
        ? 'docs/terms-cloud.pdf'
        : 'docs/terms-on-premises.pdf';
    termsOpened = false;

    // Flag per toggling visibilità delle password
    hideOldPassword = true;
    hideNewPassword = true;
    hideConfirmPassword = true;

    // Form group per la gestione dei campi di input
    form = this._fb.group({
        oldPassword: [this._data?.oldPassword ?? '', Validators.required],
        newPassword: ['', [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).*$/),
        ]],
        confirmNewPassword: ['', Validators.required],
        termsAccepted: this._fb.control(
            { value: false, disabled: this.isTermsAcceptanceRequired },
            this.isTermsAcceptanceRequired ? Validators.requiredTrue : []
        ),
    });

    constructor() {
        const confirmPassword = this.form.controls.confirmNewPassword;

        confirmPassword.addValidators(() =>
            this.passwordsDoNotMatch ? { passwordMismatch: true } : null
        );

        this.form.controls.newPassword.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(() => confirmPassword.updateValueAndValidity({ emitEvent: false }));
    }

    /**
     * Getter che calcola la forza della nuova password inserita
     * @returns Oggetto PasswordStrength con score, label e colorClass
     */
    get strength(): PasswordStrength {
        return getPasswordStrength(this.form.controls.newPassword.value);
    }

    /**
     * Getter che verifica se la nuova password e la conferma non corrispondono
     * @returns true se le password non coincidono e la conferma è stata compilata
     */
    get passwordsDoNotMatch(): boolean {
        const newPassword = this.form.controls.newPassword.value;
        const confirmPassword = this.form.controls.confirmNewPassword.value;
        return confirmPassword.length > 0 && newPassword !== confirmPassword;
    }

    get missingPasswordCriteria(): string[] {
        const password = this.form.controls.newPassword.value;
        const missing: string[] = [];

        if (password.length < 8) missing.push('at least 8 characters');
        if (!/[A-Z]/.test(password)) missing.push('one uppercase letter');
        if (!/[a-z]/.test(password)) missing.push('one lowercase letter');
        if (!/[0-9]/.test(password)) missing.push('one number');
        if (!/[^A-Za-z0-9]/.test(password)) missing.push('one special character');

        return missing;
    }

    get passwordCriteria(): { label: string; met: boolean }[] {
        const password = this.form.controls.newPassword.value;

        return [
            { label: '8+ characters', met: password.length >= 8 },
            { label: 'Uppercase', met: /[A-Z]/.test(password) },
            { label: 'Lowercase', met: /[a-z]/.test(password) },
            { label: 'Number', met: /[0-9]/.test(password) },
            { label: 'Special character', met: /[^A-Za-z0-9]/.test(password) },
        ];
    }

    get canConfirm(): boolean {
        const termsAccepted = !this.isTermsAcceptanceRequired
            || (this.termsOpened && this.form.controls.termsAccepted.value);

        return this.form.valid && !this.passwordsDoNotMatch && termsAccepted;
    }

    markTermsAsOpened(): void {
        this.termsOpened = true;
        this.form.controls.termsAccepted.enable();
    }

    /**
     * Chiude il dialog senza restituire risultati
     */
    close(): void {
        this._dialogRef.close();
    }

    /**
     * Valida il form e chiude il dialog restituendo i dati inseriti
     * Verifica che il form sia valido e che le password corrispondano
     */
    confirm(): void {
        if (!this.canConfirm) {
            this.form.markAllAsTouched();
            return;
        }

        this._dialogRef.close({
            oldPassword: this.form.controls.oldPassword.value,
            newPassword: this.form.controls.newPassword.value,
        });
    }
}
